"use client";
import { useUiControlLabels } from "@/components/i18n/ui-control-labels-context"


import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AgentRoleBadge } from "@/components/agents/shared/agent-role-badge";
import { AGENT_PRIMARY_ROLE_USE_CASE } from "@/lib/ai/roles/types";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowLeft,
  Plus,
  Search,
  User,
  Settings,
  AlertCircle,
  CheckCircle,
  XCircle,
  Play,
  Save,
  ScrollText,
  PhoneOutgoing,
  Loader2,
  GitBranch,
  MessageSquare,
} from "lucide-react";
import { PageLoader } from "@/components/ui/page-loader";
import { useVoiceAgents, type VoiceAgent } from "@/hooks/agents/voice";
import { useTwilioIntegration } from "@/hooks/integrations/use-twilio-integration";
import { useVoices } from "@/hooks/voice";
import { useAuth } from "@/hooks/use-auth";
import { VoiceAgentSyncNotice } from "./voice-agent-sync-notice";
import { useVoiceSetupRefresh } from "@/hooks/agents/voice/use-setup-refresh";
import { AgentPreviewDialog } from "./agent-preview-dialog";
import { AgentRecentCallsDialog } from "./agent-recent-calls-dialog";
import { OutgoingCallDialog } from "./outgoing-call-dialog";
import {
  GeneralSettings,
  IntegrationsSettings,
  KnowledgeBaseSettings,
  ToolsSettings,
  DangerZoneSettings,
} from "./settings";
import { usePermissions } from "@/hooks/use-permissions";
import { VOICE_AGENTS_PERMISSIONS, CALLS_PERMISSIONS } from "@/lib/auth/permissions.constants";
import { QAFeedbackLinkButton } from "@/components/ai/feedback/qa-feedback-link-button";
import { AgentsGrid } from "@/components/agents/shared/agents-grid";
import { AgentIntegrationBadges } from "@/components/agents/shared/agent-integration-badges";
import { VoiceGenderIcon } from "@/components/agents/voice/voice-gender-icon";
import { useAgentIntegrationStatusesQuery } from "@/hooks/queries/agent-integration-statuses";
import { useBillingOverviewQuery } from "@/hooks/queries/billing-overview";
import { usePlanFeatureAccessQuery } from "@/hooks/queries/plan-feature-access";
import { featureLockMessage } from "@/lib/billing/feature-access/rules";
import { BILLING_PERMISSIONS } from "@/lib/auth/permissions.constants";
import { shouldShowFreePlanVoiceNotice } from "@/lib/agents/voice/free-plan-notice";
import { AgentCreationWizard } from "@/components/agents/creation/agent-creation-wizard"
import { useTranslations } from "next-intl"




export function VoiceAgentsPage() {
  const {locale: uiFormattingLocale} = useUiControlLabels()



  const tUi = useTranslations("Voice.AgentsVoiceVoiceAgentsPage")
  const tCommonUi = useTranslations("Common")

  const router = useRouter();
  const searchParams = useSearchParams();
  const legacySettingsParam = searchParams.get("agentSettings");
  const editorAgentParam = searchParams.get("agentId") ?? legacySettingsParam;
  const editorSection =
    searchParams.get("section") ?? (legacySettingsParam ? "advanced" : null);

  const { agents, isLoading, error, refreshAgents, setAgentStatus } = useVoiceAgents();
  useVoiceSetupRefresh(agents, refreshAgents);
  const { isLoading: isLoadingTwilio } = useTwilioIntegration();
  const { getVoice } = useVoices();
  const { fetchWithAuth } = useAuth();

  const [searchTerm, setSearchTerm] = useState("");
  const [agentFlowCounts, setAgentFlowCounts] = useState<Record<string, number>>({});
  const [feedbackCounts, setFeedbackCounts] = useState<Record<string, number>>({});
  const [previewAgent, setPreviewAgent] = useState<VoiceAgent | null>(null);
  const [recentCallsAgent, setRecentCallsAgent] = useState<VoiceAgent | null>(null);
  const [outgoingCallAgent, setOutgoingCallAgent] = useState<VoiceAgent | null>(null);
  const [isSavingAgent, setIsSavingAgent] = useState(false);
  const [isUpdatingAgentStatus, setIsUpdatingAgentStatus] = useState(false);
  const [statusMessage, setStatusMessage] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const pageTopRef = useRef<HTMLDivElement>(null);

  const { can } = usePermissions();
  const billingOverview = useBillingOverviewQuery();

  // Use permissions instead of role checks
  const canViewAgents = can(VOICE_AGENTS_PERMISSIONS.VIEW);
  const canManageAgents = can(VOICE_AGENTS_PERMISSIONS.MANAGE);
  const canViewCalls = can(CALLS_PERMISSIONS.VIEW);
  const canManageBilling = can(BILLING_PERMISSIONS.MANAGE);
  const showFreePlanNotice = (agent: VoiceAgent) =>
    shouldShowFreePlanVoiceNotice(agent, billingOverview.data);
  // The page stays open for existing agents; new ones need voice in the plan.
  const createLockHint = featureLockMessage("voice", usePlanFeatureAccessQuery().data?.voice);

  const handleCopyAgentId = useCallback(async (agentId: string) => {
    try {
      await navigator.clipboard.writeText(agentId);
      toast.success(tUi("copied"));
    } catch {
      toast.error(tUi("unableToCopyAgentID"));
    }
  }, [tUi]);

  const isCreatingAgent = editorAgentParam === "new";
  const editorAgentId = editorAgentParam && !isCreatingAgent ? editorAgentParam : null;
  const editorAgent = editorAgentId
    ? agents.find((agent) => agent.id === editorAgentId)
    : null;
  type SettingsTab = "general" | "integrations" | "knowledge" | "tools" | "danger";
  const [activeTab, setActiveTab] = useState<SettingsTab>(() => {
    if (isCreatingAgent) return "general";
    if (
      editorSection === "crm"
      || editorSection === "delivery"
      || editorSection === "integrations"
    ) return "integrations";
    if (editorSection === "knowledge") return "knowledge";
    if (editorSection === "tools") return "tools";
    if (editorSection === "danger") return "danger";
    return "general";
  });

  const filteredAgents = useMemo(
    () =>
      agents.filter(
        (agent) =>
          agent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          agent.languages.some((language) =>
            language.toLowerCase().includes(searchTerm.toLowerCase()),
          ),
      ),
    [agents, searchTerm],
  );

  const handleCreateAgent = () => {
    router.push("?page=voice-agents&agentId=new");
  };

  const handleEditAgent = (agent: VoiceAgent) => {
    router.push(`?page=voice-agents&agentId=${agent.id}`);
  };

  const handleBackToList = () => {
    router.push("?page=voice-agents");
  };

  const handleAgentSaved = (savedAgent: VoiceAgent) => {
    refreshAgents();

    if (editorAgentParam === "new") {
      router.push(`?page=voice-agents&agentId=${savedAgent.id}`);
      setStatusMessage({
        type: "success",
        message: tUi("agentCreatedSuccessfully"),
      });
    } else {
      setStatusMessage({
        type: "success",
        message: tUi("agentUpdatedSuccessfully"),
      });
    }

    // Scroll to top to show the success message
    // Use setTimeout to ensure the message is rendered first
    setTimeout(() => {
      pageTopRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const handleAgentStatusChange = async (agentId: string, status: "active" | "disabled") => {
    setIsUpdatingAgentStatus(true);
    try {
      await setAgentStatus(agentId, status);
      toast.success(status === "active" ? "Voice Agent enabled" : "Voice Agent disabled");
    } catch (statusError) {
      toast.error(statusError instanceof Error ? statusError.message : "Could not update Voice Agent status");
    } finally {
      setIsUpdatingAgentStatus(false);
    }
  };

  useEffect(() => {
    if (statusMessage) {
      const timer = setTimeout(() => setStatusMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [statusMessage]);

  // Load flow counts for agents - use agent IDs as dependency to reload when agents change
  const agentIdList = useMemo(() => agents.map(agent => agent.id), [agents]);
  const agentIds = useMemo(() => agentIdList.toSorted().join(','), [agentIdList]);
  const integrationStatusesQuery = useAgentIntegrationStatusesQuery(
    "voice_agent",
    agentIdList,
    !editorAgentParam,
  );

  useEffect(() => {
    async function loadFlowCounts() {
      try {
        const response = await fetchWithAuth('/api/agents/voice/flows-count');
        if (response.ok) {
          const data = await response.json();
          setAgentFlowCounts(data.counts || {});
        }
      } catch {
        // Ignore errors - badges are optional
      }
    }

    if (agentIds && !editorAgentParam) {
      loadFlowCounts();
    }
  }, [agentIds, editorAgentParam, fetchWithAuth]);

  // Load feedback counts for all agents (only pending - not taken in progress)
  const loadFeedbackCounts = useCallback(async () => {
    if (agents.length === 0) return;

    const counts: Record<string, number> = {};

    await Promise.all(
      agents.map(async (agent) => {
        try {
          const res = await fetchWithAuth(
            `/api/ai/feedback?agent_type=voice_agent&status=pending&agent_id=${agent.id}&limit=1`
          );
          const data = await res.json();

          if (data.total > 0) {
            counts[agent.id] = data.total;
          }
        } catch (err) {
          console.error(`Failed to load feedback count for agent ${agent.id}:`, err);
        }
      })
    );

    setFeedbackCounts(counts);
  }, [fetchWithAuth, agents]);

  useEffect(() => {
    if (agentIds && !editorAgentParam) {
      loadFeedbackCounts();
    }
  }, [agentIds, editorAgentParam, loadFeedbackCounts]);

  useEffect(() => {
    if (!editorAgentParam) {
      return;
    }

    if (isCreatingAgent) {
      setActiveTab("general");
      return;
    }

    if (
      editorSection === "crm"
      || editorSection === "delivery"
      || editorSection === "integrations"
    ) {
      setActiveTab("integrations");
    } else if (editorSection === "knowledge") {
      setActiveTab("knowledge");
    } else if (editorSection === "tools") {
      setActiveTab("tools");
    } else if (editorSection === "danger") {
      setActiveTab("danger");
    } else {
      setActiveTab("general");
    }
  }, [editorSection, editorAgentParam, isCreatingAgent]);

  useEffect(() => {
    if (activeTab === "general" || isLoading || isLoadingTwilio) {
      return;
    }

    // Scroll to top when switching to settings tabs
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [activeTab, isLoading, isLoadingTwilio]);

  const getStatusBadge = (status: VoiceAgent["status"]) => {
    switch (status) {
      case "active":
      case "draft":
        // Both active and draft (legacy) are treated as active/ready
        return (
          <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
            <CheckCircle className="w-3 h-3 mr-1" />
            <span><span key={tUi("active")}>{tUi("active")}</span></span>
          </Badge>
        );
      case "error":
        return (
          <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
            <XCircle className="w-3 h-3 mr-1" />
            <span><span key={tUi("error")}>{tUi("error")}</span></span>
          </Badge>
        );
      case "disabled":
        return (
          <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">
            <AlertCircle className="w-3 h-3 mr-1" />
            <span><span key={tUi("disabled")}>{tUi("disabled")}</span></span>
          </Badge>
        );
      default:
        return null;
    }
  };

  const getLanguageFlag = (language: string) => {
    const flags: Record<string, string> = {
      english: "🇺🇸",
      en: "🇺🇸",
      spanish: "🇪🇸",
      es: "🇪🇸",
      french: "🇫🇷",
      fr: "🇫🇷",
      german: "🇩🇪",
      de: "🇩🇪",
      italian: "🇮🇹",
      it: "🇮🇹",
      portuguese: "🇵🇹",
      pt: "🇵🇹",
      russian: "🇷🇺",
      ru: "🇷🇺",
      ukrainian: "🇺🇦",
      uk: "🇺🇦",
      japanese: "🇯🇵",
      ja: "🇯🇵",
      korean: "🇰🇷",
      ko: "🇰🇷",
      chinese: "🇨🇳",
      zh: "🇨🇳",
    };
    return flags[language.toLowerCase()] || "🌐";
  };

  const agentActionDialogs = (
    <>
      {previewAgent && (
        <AgentPreviewDialog
          prepareOnStart={canManageAgents && !previewAgent.external_id}
          agentId={previewAgent.id}
          agentName={previewAgent.name}
          open={!!previewAgent}
          onOpenChange={(open) => {
            if (open) return;
            setPreviewAgent(null);
            if (!previewAgent.external_id) void refreshAgents();
          }}
        />
      )}
      <OutgoingCallDialog
        agent={outgoingCallAgent}
        open={!!outgoingCallAgent}
        onOpenChange={(open) => !open && setOutgoingCallAgent(null)}
      />
    </>
  );

  if (editorAgentParam) {
    // Check manage permission for settings access
    if (!canManageAgents) {
      return (
        <div className="p-6 max-w-5xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle><span key={tUi("accessDenied")}>{tUi("accessDenied")}</span></CardTitle>
            </CardHeader>
            <CardContent>
              <Alert>
                <AlertDescription>
                  <span key={tUi("youDonTHavePermissionToManageVoice")}>{tUi("youDonTHavePermissionToManageVoice")}</span></AlertDescription>
              </Alert>
              <Button
                variant="outline"
                className="mt-4"
                onClick={handleBackToList}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                <span><span key={tUi("backToAgents")}>{tUi("backToAgents")}</span></span>
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    if (isLoading || isLoadingTwilio) {
      return (
        <div className="p-6 max-w-5xl mx-auto">
          <PageLoader message={tUi("loadingAgent")} />
        </div>
      );
    }

    if (!isCreatingAgent && !editorAgent) {
      return (
        <div className="p-6 space-y-6 max-w-4xl mx-auto">
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-4" />
            <h3 className="text-lg font-medium"><span key={tUi("agentNotFound")}>{tUi("agentNotFound")}</span></h3>
            <p className="text-muted-foreground mt-2">
              <span key={tUi("theAgentYouReLookingForDoesnT")}>{tUi("theAgentYouReLookingForDoesnT")}</span></p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={handleBackToList}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              <span><span key={tUi("backToAgents")}>{tUi("backToAgents")}</span></span>
            </Button>
          </div>
        </div>
      );
    }

    if (isCreatingAgent) {
      return <AgentCreationWizard type="voice" onCancel={handleBackToList} onCreated={id => { void refreshAgents(); router.push(`?page=voice-agents&agentId=${id}`) }} />
    }

    return (
      <div className="mx-auto max-w-5xl space-y-6 p-4 sm:p-6">
        <div ref={pageTopRef} className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          <div className="flex min-w-0 items-start gap-2 sm:gap-4">
            <Button variant="ghost" size="icon" className="shrink-0" onClick={handleBackToList}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                <h1 key={isCreatingAgent
                    ? tUi("createVoiceAgent")
                    : tUi("editValue0", { value0: String(editorAgent?.name) })} className="break-words text-xl font-semibold text-foreground sm:text-2xl">
                  {isCreatingAgent
                    ? tUi("createVoiceAgent")
                    : tUi("editValue0", { value0: String(editorAgent?.name) })}
                </h1>
              </div>
              <p key={isCreatingAgent
                  ? tUi("setUpTheBasicConfigurationForYourNew")
                  : tUi("updateAgentDetailsAndManageAdvancedSettings")} className="text-muted-foreground mt-1">
                {isCreatingAgent
                  ? tUi("setUpTheBasicConfigurationForYourNew")
                  : tUi("updateAgentDetailsAndManageAdvancedSettings")}
              </p>
            </div>
          </div>
          {editorAgentId && editorAgent && (
            <QAFeedbackLinkButton
              agentId={editorAgentId}
              agentType="voice_agent"
              agentName={editorAgent.name}
              className="w-full justify-center sm:w-auto"
            />
          )}
        </div>

        {editorAgent && showFreePlanNotice(editorAgent) ? (
          <FreePlanVoiceNotice canManageBilling={canManageBilling} />
        ) : null}

        {statusMessage && (
          <Alert
            className={
              statusMessage.type === "success"
                ? "border-green-200 bg-green-50"
                : "border-red-200 bg-red-50"
            }
          >
            <AlertDescription
              className={
                statusMessage.type === "success"
                  ? "text-green-800"
                  : "text-red-800"
              }
            >
              {statusMessage.message}
            </AlertDescription>
          </Alert>
        )}

        <Tabs
          value={activeTab}
          onValueChange={(value) => setActiveTab(value as SettingsTab)}
          className="space-y-4"
        >
          <div className="overflow-x-auto pb-2 [scrollbar-width:thin]">
            <TabsList className="grid w-max grid-cols-5 sm:w-full">
              <TabsTrigger value="general"><span key={tUi("general")}>{tUi("general")}</span></TabsTrigger>
              <TabsTrigger value="integrations" disabled={isCreatingAgent}>
                <span key={tUi("integrations")}>{tUi("integrations")}</span></TabsTrigger>
              <TabsTrigger value="knowledge" disabled={isCreatingAgent}>
                <span key={tUi("knowledge")}>{tUi("knowledge")}</span></TabsTrigger>
              <TabsTrigger value="tools" disabled={isCreatingAgent}>
                <span key={tUi("tools")}>{tUi("tools")}</span></TabsTrigger>
              <TabsTrigger value="danger" disabled={isCreatingAgent}>
                <span key={tUi("danger")}>{tUi("danger")}</span></TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="general" className="space-y-4 pb-32 sm:pb-20">
            <GeneralSettings
              agent={isCreatingAgent ? null : (editorAgent ?? null)}
              isCreating={isCreatingAgent}
              onSave={handleAgentSaved}
              onSavingChange={setIsSavingAgent}
              onStatusChange={handleAgentStatusChange}
              isUpdatingStatus={isUpdatingAgentStatus}
            />
          </TabsContent>

          <TabsContent value="integrations" className="space-y-4 pb-20">
            {editorAgentId && editorAgent && (
              <IntegrationsSettings
                agentId={editorAgentId}
                agentName={editorAgent.name}
              />
            )}
          </TabsContent>

          <TabsContent value="knowledge" className="space-y-4 pb-20">
            {editorAgentId && <KnowledgeBaseSettings agentId={editorAgentId} />}
          </TabsContent>

          <TabsContent value="tools" className="space-y-4 pb-20">
            {editorAgentId && <ToolsSettings agentId={editorAgentId} />}
          </TabsContent>

          <TabsContent value="danger" className="space-y-4 pb-20">
            {editorAgentId && editorAgent && (
              <DangerZoneSettings
                agentId={editorAgentId}
                agentName={editorAgent.name}
                onDelete={refreshAgents}
              />
            )}
          </TabsContent>
        </Tabs>

        {/* Sticky Footer - only for General tab */}
        {activeTab === "general" && (
          <div className="fixed bottom-0 left-0 right-0 z-50 border-t bg-background p-3 shadow-lg md:left-64 md:p-4">
            <div className="mx-auto flex max-w-5xl flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3 md:pr-48">
              <div className="grid grid-cols-2 gap-2 sm:flex sm:gap-3">
                {!isCreatingAgent && editorAgent && (
                  <>
                    <Button type="button" variant="outline" onClick={() => setPreviewAgent(editorAgent)}
                      disabled={isSavingAgent || editorAgent.status === "error" || editorAgent.status === "disabled"}
                      title={editorAgent.status === "error" || editorAgent.status === "disabled" ? tUi("fixAgentErrorsFirst") : tUi("testAgent")}>
                      <Play className="mr-2 h-4 w-4" />
                      <span><span key={tUi("test")}>{tUi("test")}</span></span>
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setOutgoingCallAgent(editorAgent)}
                      disabled={isSavingAgent || editorAgent.status === "error" || editorAgent.status === "disabled"}
                      title={editorAgent.status === "error" || editorAgent.status === "disabled" ? tUi("fixAgentErrorsFirst") : tUi("makeAnOutgoingCall")}>
                      <PhoneOutgoing className="mr-2 h-4 w-4" />
                      <span><span key={tUi("makeACall")}>{tUi("makeACall")}</span></span>
                    </Button>
                  </>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2 sm:flex sm:gap-3">
                <Button className="w-full sm:w-auto" variant="outline" onClick={handleBackToList} disabled={isSavingAgent}>
                  <span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></Button>
                <Button
                  type="submit"
                  form="agent-form"
                  className="w-full bg-blue-600 hover:bg-blue-700 sm:w-auto"
                  disabled={isSavingAgent}
                >
                  {isSavingAgent ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      <span key={isCreatingAgent ? tUi("creating") : tUi("updating")}>{isCreatingAgent ? tUi("creating") : tUi("updating")}</span>
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      <span key={isCreatingAgent ? tUi("createAgent") : tUi("updateAgent")}>{isCreatingAgent ? tUi("createAgent") : tUi("updateAgent")}</span>
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
        {agentActionDialogs}
      </div>
    );
  }

  if (!canViewAgents) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle><span key={tUi("accessDenied")}>{tUi("accessDenied")}</span></CardTitle>
          </CardHeader>
          <CardContent>
            <Alert>
              <AlertDescription>
                <span key={tUi("youDonTHavePermissionToAccessVoice")}>{tUi("youDonTHavePermissionToAccessVoice")}</span></AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading || isLoadingTwilio) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <PageLoader message={tUi("loadingAgents")} />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold text-foreground">
            <span key={tUi("voiceAgents")}>{tUi("voiceAgents")}</span></h1>
          <p className="text-muted-foreground mt-1">
            <span key={tUi("manageYourVoiceAgentsAndPhoneNumberConfigurations")}>{tUi("manageYourVoiceAgentsAndPhoneNumberConfigurations")}</span></p>
        </div>
        <div className="flex flex-col items-end gap-1">
          {canManageAgents && (
            <Button
              onClick={handleCreateAgent}
              disabled={createLockHint !== null}
            >
              <Plus className="w-4 h-4 mr-2" />
              <span><span key={tUi("createAgent")}>{tUi("createAgent")}</span></span>
            </Button>
          )}
          {canManageAgents && createLockHint && (
            <p className="max-w-xs text-right text-xs text-muted-foreground">{createLockHint}</p>
          )}
        </div>
      </div>

      {/* Status Messages */}
      {statusMessage && (
        <Alert
          className={
            statusMessage.type === "success"
              ? "border-green-200 bg-green-50"
              : "border-red-200 bg-red-50"
          }
        >
          <AlertDescription
            className={
              statusMessage.type === "success"
                ? "text-green-800"
                : "text-red-800"
            }
          >
            {statusMessage.message}
          </AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert className="border-red-200 bg-red-50">
          <AlertCircle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-800">{error}</AlertDescription>
        </Alert>
      )}


      {/* Search */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder={tUi("searchAgentsByNameLanguageOrPhoneNumber")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="text-sm text-muted-foreground">
            {filteredAgents.length} <span><span key={tUi("of")}>{tUi("of")}</span></span> {agents.length} <span><span key={tUi("agents")}>{tUi("agents")}</span></span>
          </div>
        </div>
      </div>

      {/* Agents Grid */}
      {filteredAgents.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
            <User className="w-8 h-8 text-muted-foreground" />
          </div>
          <h3 key={agents.length === 0 ? tUi("noVoiceAgentsYet") : tUi("noAgentsFound")} className="text-lg font-medium text-foreground mb-2">
            {agents.length === 0 ? tUi("noVoiceAgentsYet") : tUi("noAgentsFound")}
          </h3>
          <p key={agents.length === 0
              ? tUi("createYourFirstVoiceAgentToStartHandling")
              : tUi("tryAdjustingYourSearchTerms")} className="text-gray-600 mb-6">
            {agents.length === 0
              ? tUi("createYourFirstVoiceAgentToStartHandling")
              : tUi("tryAdjustingYourSearchTerms")}
          </p>
          {canManageAgents && agents.length === 0 && (
            <Button
              onClick={handleCreateAgent}
              disabled={createLockHint !== null}
            >
              <Plus className="w-4 h-4 mr-2" />
              <span><span key={tUi("createYourFirstAgent")}>{tUi("createYourFirstAgent")}</span></span>
            </Button>
          )}
        </div>
      ) : (
        <AgentsGrid>
          {filteredAgents.map((agent) => (
            <Card key={agent.id} className="flex h-full flex-col transition-shadow hover:shadow-md">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1 pr-3">
                    <CardTitle className="text-lg font-medium text-gray-900 mb-1 truncate">
                      {agent.name}
                    </CardTitle>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-500">
                      {agent.languages.map((language) => (
                        <span key={language} className="inline-flex items-center gap-1">
                          <span>{getLanguageFlag(language)}</span>
                          <span className="uppercase">{language}</span>
                        </span>
                      ))}
                    </div>
                    <p
                      className="mt-1 flex min-w-0 cursor-pointer items-center gap-1 whitespace-nowrap font-mono text-xs text-gray-500 hover:text-gray-700"
                      role="button"
                      tabIndex={0}
                      title={tUi("agentIDValue0", { value0: String(agent.id) })}
                      onClick={() => handleCopyAgentId(agent.id)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          void handleCopyAgentId(agent.id);
                        }
                      }}
                    >
                      <span className="shrink-0"><span key={tUi("agentID")}>{tUi("agentID")}</span></span>
                      <span className="truncate">{agent.id}</span>
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <div className="flex items-center gap-2">
                    {feedbackCounts[agent.id] > 0 && (
                      <Badge
                        className="bg-orange-500 text-white text-xs px-1.5 py-0.5 cursor-pointer hover:bg-orange-600 transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          router.push(`/?page=qa-feedback&voiceAgentId=${agent.id}&agentName=${encodeURIComponent(agent.name)}`);
                        }}
                        title={tUi("viewQAFeedback")}
                      >
                        <MessageSquare className="w-3 h-3 mr-1" />
                        {feedbackCounts[agent.id]}
                      </Badge>
                    )}
                    {agentFlowCounts[agent.id] > 0 && (
                      <Badge variant="outline" className="text-xs gap-1">
                        <GitBranch className="w-3 h-3" />
                        {agentFlowCounts[agent.id]} <span><span key={tUi("flow")}>{tUi("flow")}</span></span><span>{agentFlowCounts[agent.id] === 1 ? '' : 's'}</span>
                      </Badge>
                    )}
                    {getStatusBadge(agent.status)}
                    </div>
                    <AgentRoleBadge
                      useCase={AGENT_PRIMARY_ROLE_USE_CASE.voice_agent}
                      roleSlugs={agent.config?.roles}
                      roleLabel={agent.config?.role_label}
                    />
                    <AgentIntegrationBadges
                      crmConnected={integrationStatusesQuery.data?.[agent.id]?.crmConnected ?? false}
                      deliveryConnected={integrationStatusesQuery.data?.[agent.id]?.deliveryConnected ?? false}
                      metaAdsConnected={integrationStatusesQuery.data?.[agent.id]?.metaAdsConnected ?? false}
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="flex flex-1 flex-col pt-0">
                <div className="flex h-full flex-1 flex-col gap-3">
                  {showFreePlanNotice(agent) ? (
                    <FreePlanVoiceNotice canManageBilling={canManageBilling} compact />
                  ) : null}
                  {/* Voice */}
                  {agent.voice_id && (
                    <div className="flex items-center gap-2 text-sm">
                      <VoiceGenderIcon
                        labels={getVoice(agent.voice_id)?.labels}
                        className="h-4 w-4 text-gray-400"
                      />
                      <span className="text-gray-600">
                        {getVoice(agent.voice_id)?.name || agent.voice_id}
                      </span>
                    </div>
                  )}

                  {/* First message preview */}
                  {agent.first_message && (
                    <div className="text-sm text-gray-500 line-clamp-2">
                      <span>"</span>{agent.first_message}<span>"</span>
                    </div>
                  )}

                  <VoiceAgentSyncNotice message={agent.last_sync_error} />

                  {/* Dates */}
                  <div className="mt-auto border-t pt-2 text-xs text-gray-500">
                    <div>
                      <span><span key={tUi("created")}>{tUi("created")}</span></span> {new Date(agent.created_at).toLocaleDateString(uiFormattingLocale)}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="grid grid-cols-1 gap-2 pt-5 sm:grid-cols-2">
                    {canManageAgents && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() => handleEditAgent(agent)}
                      >
                        <Settings className="w-4 h-4 mr-1" />
                        <span><span key={tUi("settings")}>{tUi("settings")}</span></span>
                      </Button>
                    )}
                    {/* Preview uses voice_agents.view - same as API endpoint */}
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => setPreviewAgent(agent)}
                      disabled={agent.status === "error" || agent.status === "disabled"}
                      title={
                        agent.status === "error" || agent.status === "disabled"
                          ? tUi("fixAgentErrorsFirst")
                          : tUi("testAgent")
                      }
                    >
                      <Play className="w-4 h-4 mr-1" />
                      <span><span key={tUi("test")}>{tUi("test")}</span></span>
                    </Button>
                    {canManageAgents && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOutgoingCallAgent(agent);
                        }}
                        disabled={agent.status === "error" || agent.status === "disabled"}
                        title={
                          agent.status === "error" || agent.status === "disabled"
                            ? tUi("fixAgentErrorsFirst")
                            : tUi("makeAnOutgoingCall")
                        }
                      >
                        <PhoneOutgoing className="w-4 h-4 mr-1" />
                        <span><span key={tUi("makeACall")}>{tUi("makeACall")}</span></span>
                      </Button>
                    )}
                    {canViewCalls && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={(e) => {
                          e.stopPropagation();
                          router.push(`?page=call-logs&agentId=${agent.id}`);
                        }}
                        title={tUi("viewCallLogs")}
                      >
                        <ScrollText className="w-4 h-4 mr-1" />
                        <span><span key={tUi("callLogs")}>{tUi("callLogs")}</span></span>
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setRecentCallsAgent(agent);
                      }}
                      title={tUi("addFeedbackToRecentCalls")}
                      className="w-full text-orange-500 hover:text-orange-600 hover:bg-orange-50"
                    >
                      <MessageSquare className="w-4 h-4 mr-1" />
                      <span><span key={tUi("feedback")}>{tUi("feedback")}</span></span>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </AgentsGrid>
      )}

      {agentActionDialogs}

      {/* Recent Calls Dialog for Feedback */}
      {recentCallsAgent && (
        <AgentRecentCallsDialog
          agentId={recentCallsAgent.id}
          agentName={recentCallsAgent.name}
          open={!!recentCallsAgent}
          onOpenChange={(open) => !open && setRecentCallsAgent(null)}
          onFeedbackAdded={loadFeedbackCounts}
        />
      )}

    </div>
  );
}

function FreePlanVoiceNotice({ canManageBilling, compact = false }: {
  canManageBilling: boolean;
  compact?: boolean;
}) {
  const tUi = useTranslations("Voice.AgentsVoiceVoiceAgentsPage")

  return (
    <Alert className="border-amber-200 bg-amber-50 text-amber-950">
      <AlertCircle className="h-4 w-4 text-amber-700" />
      <AlertDescription className={compact ? "space-y-2 text-xs" : "flex flex-wrap items-center justify-between gap-2 text-sm"}>
        <span><span key={tUi("voiceAgentsArenTAvailableOnYourFree")}>{tUi("voiceAgentsArenTAvailableOnYourFree")}</span></span>
        {canManageBilling ? (
          <Button asChild size="sm" variant="outline" className="border-amber-300 bg-white">
            <Link href="/?page=billing"><span key={tUi("upgradePlan")}>{tUi("upgradePlan")}</span></Link>
          </Button>
        ) : (
          <span><span key={tUi("askABillingAdminToUpgradeYourPlan")}>{tUi("askABillingAdminToUpgradeYourPlan")}</span></span>
        )}
      </AlertDescription>
    </Alert>
  );
}
